<template>
  <div class="pagination-wrapper">
    <div class="pagination-info">
      <span class="page-info">Página {{ value }} de {{ totalPages }}</span>
      <div class="goto-page">
        <input 
          type="number" 
          v-model.number="gotoPageInput"
          @keyup.enter="goToPage"
          min="1"
          :max="totalPages"
          placeholder="Ir a..."
          class="goto-input"
        />
        <button @click="goToPage" class="goto-btn" :disabled="!isValidPage">
          <i class="tim-icons icon-minimal-right"></i>
        </button>
      </div>
    </div>
    
    <div class="pagination-controls">
      <ul class="pagination" :class="paginationClass">
        <!-- Primera página -->
        <li class="page-item prev-page"
            v-if="showArrows"
            :class="{ disabled: value === 1 }">
          <a class="page-link" aria-label="First" @click="changePage(1)" :title="'Primera página'">
            <i class="tim-icons icon-double-left" aria-hidden="true"></i>
          </a>
        </li>
        
        <!-- Página anterior -->
        <li class="page-item prev-page"
            v-if="showArrows"
            :class="{ disabled: value === 1 }">
          <a class="page-link" aria-label="Previous" @click="prevPage" :title="'Página anterior'">
            <i class="tim-icons icon-minimal-left" aria-hidden="true"></i>
          </a>
        </li>
        
        <!-- Primera página si no está visible -->
        <li class="page-item" v-if="showFirstPage">
          <a class="page-link" @click="changePage(1)">1</a>
        </li>
        
        <!-- Elipsis inicial -->
        <li class="page-item disabled" v-if="showFirstEllipsis">
          <span class="page-link">...</span>
        </li>
        
        <!-- Páginas visibles -->
        <li class="page-item"
            v-for="item in visiblePages"
            :key="item"
            :class="{ active: value === item }">
          <a class="page-link" @click="changePage(item)">{{ item }}</a>
        </li>
        
        <!-- Elipsis final -->
        <li class="page-item disabled" v-if="showLastEllipsis">
          <span class="page-link">...</span>
        </li>
        
        <!-- Última página si no está visible -->
        <li class="page-item" v-if="showLastPage">
          <a class="page-link" @click="changePage(totalPages)">{{ totalPages }}</a>
        </li>
        
        <!-- Página siguiente -->
        <li v-if="showArrows"
            class="page-item page-pre next-page"
            :class="{ disabled: value === totalPages }">
          <a class="page-link" aria-label="Next" @click="nextPage" :title="'Página siguiente'">
            <i class="tim-icons icon-minimal-right" aria-hidden="true"></i>
          </a>
        </li>
        
        <!-- Última página -->
        <li v-if="showArrows"
            class="page-item next-page"
            :class="{ disabled: value === totalPages }">
          <a class="page-link" aria-label="Last" @click="changePage(totalPages)" :title="'Última página'">
            <i class="tim-icons icon-double-right" aria-hidden="true"></i>
          </a>
        </li>
      </ul>
    </div>
  </div>
</template>
<script>
  export default {
    name: 'base-pagination',
    props: {
      type: {
        type: String,
        default: 'primary',
        validator: value => {
          return [
            'default',
            'primary',
            'danger',
            'success',
            'warning',
            'info'
          ].includes(value);
        }
      },
      pageCount: {
        type: Number,
        default: 0
      },
      perPage: {
        type: Number,
        default: 10
      },
      showArrows: {
        type: Boolean,
        default: true
      },
      total: {
        type: Number,
        default: 0
      },
      value: {
        type: Number,
        default: 1
      },
      pagesToDisplay: {
        type: Number,
        default: 5
      }
    },
    data() {
      return {
        gotoPageInput: null
      };
    },
    computed: {
      paginationClass() {
        return `pagination-${this.type}`;
      },
      totalPages() {
        if (this.pageCount > 0) return this.pageCount;
        if (this.total > 0) {
          return Math.ceil(this.total / this.perPage);
        }
        return 1;
      },
      defaultPagesToDisplay() {
        // Ajuste responsive: menos páginas en móvil
        const basePages = window.innerWidth < 576 ? 3 : this.pagesToDisplay;
        if (this.totalPages > 0 && this.totalPages < basePages) {
          return this.totalPages;
        }
        return basePages;
      },
      visiblePages() {
        const pages = [];
        const halfVisible = Math.floor(this.defaultPagesToDisplay / 2);
        
        let start = Math.max(2, this.value - halfVisible);
        let end = Math.min(this.totalPages - 1, this.value + halfVisible);
        
        // Ajustar si estamos cerca del inicio
        if (this.value <= halfVisible + 1) {
          end = Math.min(this.totalPages - 1, this.defaultPagesToDisplay);
          start = 2;
        }
        
        // Ajustar si estamos cerca del final
        if (this.value >= this.totalPages - halfVisible) {
          start = Math.max(2, this.totalPages - this.defaultPagesToDisplay);
          end = this.totalPages - 1;
        }
        
        // Si solo hay una o dos páginas, mostrarlas directamente
        if (this.totalPages <= 2) {
          for (let i = 1; i <= this.totalPages; i++) {
            pages.push(i);
          }
          return pages;
        }
        
        // Construir array de páginas visibles
        for (let i = start; i <= end; i++) {
          pages.push(i);
        }
        
        return pages;
      },
      showFirstPage() {
        return this.totalPages > 1 && !this.visiblePages.includes(1);
      },
      showLastPage() {
        return this.totalPages > 1 && !this.visiblePages.includes(this.totalPages);
      },
      showFirstEllipsis() {
        return this.visiblePages.length > 0 && this.visiblePages[0] > 2;
      },
      showLastEllipsis() {
        return this.visiblePages.length > 0 && this.visiblePages[this.visiblePages.length - 1] < this.totalPages - 1;
      },
      isValidPage() {
        return this.gotoPageInput && 
               this.gotoPageInput >= 1 && 
               this.gotoPageInput <= this.totalPages &&
               this.gotoPageInput !== this.value;
      }
    },
    methods: {
      ScrollTop() {
        const container = document.querySelector('.main-panel');
        if (container != undefined && container != null)
          container.scrollTop = 0;
      },
      changePage(item) {
        if (item >= 1 && item <= this.totalPages && item !== this.value) {
          this.$emit('input', item);
          this.ScrollTop();
        }
      },
      nextPage() {
        if (this.value < this.totalPages) {
          this.$emit('input', this.value + 1);
          this.ScrollTop();
        }
      },
      prevPage() {
        if (this.value > 1) {
          this.$emit('input', this.value - 1);
          this.ScrollTop();
        }
      },
      goToPage() {
        if (this.isValidPage) {
          this.changePage(this.gotoPageInput);
          this.gotoPageInput = null;
        }
      }
    },
    watch: {
      perPage() {
        this.$emit('input', 1);
      },
      total() {
        this.$emit('input', 1);
      }
    }
  };
</script>

<style scoped>
.pagination-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  width: 100%;
  padding: 0.5rem;
}

.pagination-info {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  width: 100%;
}

.page-info {
  font-size: 0.875rem;
  color: rgba(255, 255, 255, 0.8);
  font-weight: 500;
  white-space: nowrap;
}

.goto-page {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.goto-input {
  width: 70px;
  padding: 0.375rem 0.5rem;
  font-size: 0.875rem;
  border-radius: 0.25rem;
  border: 1px solid rgba(255, 255, 255, 0.2);
  background-color: rgba(255, 255, 255, 0.1);
  color: #fff;
  text-align: center;
  transition: all 0.3s ease;
}

.goto-input:focus {
  outline: none;
  border-color: rgba(29, 140, 248, 0.8);
  background-color: rgba(255, 255, 255, 0.15);
  box-shadow: 0 0 0 0.2rem rgba(29, 140, 248, 0.25);
}

.goto-input::placeholder {
  color: rgba(255, 255, 255, 0.5);
  font-size: 0.75rem;
}

/* Quitar flechas del input number en Chrome, Safari, Edge, Opera */
.goto-input::-webkit-outer-spin-button,
.goto-input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

/* Quitar flechas del input number en Firefox */
.goto-input[type=number] {
  -moz-appearance: textfield;
}

.goto-btn {
  padding: 0.375rem 0.75rem;
  font-size: 0.875rem;
  border-radius: 0.25rem;
  border: none;
  background-color: #1d8cf8;
  color: #fff;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 36px;
  height: 36px;
}

.goto-btn:hover:not(:disabled) {
  background-color: #0d7ce8;
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(29, 140, 248, 0.3);
}

.goto-btn:active:not(:disabled) {
  transform: translateY(0);
}

.goto-btn:disabled {
  background-color: rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.4);
  cursor: not-allowed;
  opacity: 0.6;
}

.pagination-controls {
  display: flex;
  justify-content: center;
  width: 100%;
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
}

.pagination-controls::-webkit-scrollbar {
  height: 4px;
}

.pagination-controls::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, 0.05);
  border-radius: 2px;
}

.pagination-controls::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.2);
  border-radius: 2px;
}

.pagination-controls::-webkit-scrollbar-thumb:hover {
  background: rgba(255, 255, 255, 0.3);
}

.pagination {
  margin: 0;
  display: flex;
  flex-wrap: nowrap;
  gap: 0.25rem;
}

.page-item {
  flex-shrink: 0;
}

.page-link {
  min-width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.375rem 0.75rem;
  cursor: pointer;
  user-select: none;
  touch-action: manipulation;
}

.page-item.disabled .page-link {
  cursor: default;
  pointer-events: none;
}

/* Responsive Styles */

/* Tablets pequeñas y móviles grandes (landscape) */
@media (max-width: 768px) {
  .pagination-wrapper {
    gap: 0.75rem;
    padding: 0.25rem;
  }
  
  .page-info {
    font-size: 0.8125rem;
  }
  
  .goto-input {
    width: 60px;
    padding: 0.3rem 0.4rem;
    font-size: 0.8125rem;
  }
  
  .goto-btn {
    padding: 0.3rem 0.6rem;
    min-width: 32px;
    height: 32px;
  }
  
  .page-link {
    min-width: 32px;
    height: 32px;
    padding: 0.3rem 0.6rem;
    font-size: 0.875rem;
  }
  
  .pagination {
    gap: 0.2rem;
  }
}

/* Móviles pequeños (portrait) */
@media (max-width: 576px) {
  .pagination-wrapper {
    gap: 0.5rem;
    padding: 0.25rem 0;
  }
  
  .pagination-info {
    flex-direction: column;
    gap: 0.5rem;
  }
  
  .page-info {
    font-size: 0.75rem;
  }
  
  .goto-page {
    gap: 0.4rem;
  }
  
  .goto-input {
    width: 55px;
    padding: 0.25rem 0.35rem;
    font-size: 0.75rem;
  }
  
  .goto-btn {
    padding: 0.25rem 0.5rem;
    min-width: 30px;
    height: 30px;
    font-size: 0.75rem;
  }
  
  .page-link {
    min-width: 30px;
    height: 30px;
    padding: 0.25rem 0.5rem;
    font-size: 0.8125rem;
  }
  
  .page-link i {
    font-size: 0.75rem;
  }
  
  .pagination {
    gap: 0.15rem;
  }
  
  .pagination-controls {
    padding: 0 0.25rem;
  }
}

/* Móviles muy pequeños */
@media (max-width: 360px) {
  .page-info {
    font-size: 0.7rem;
  }
  
  .goto-input {
    width: 50px;
    font-size: 0.7rem;
  }
  
  .page-link {
    min-width: 28px;
    height: 28px;
    padding: 0.2rem 0.4rem;
    font-size: 0.75rem;
  }
  
  .goto-btn {
    min-width: 28px;
    height: 28px;
  }
}

/* Tablets y pantallas medianas */
@media (min-width: 769px) and (max-width: 1024px) {
  .page-link {
    min-width: 38px;
    height: 38px;
    padding: 0.4rem 0.8rem;
  }
  
  .goto-input {
    width: 65px;
  }
  
  .goto-btn {
    min-width: 38px;
    height: 38px;
  }
}

/* Pantallas grandes (desktop) */
@media (min-width: 1025px) {
  .page-link {
    min-width: 40px;
    height: 40px;
    transition: all 0.3s ease;
  }
  
  .page-link:hover {
    transform: translateY(-2px);
  }
}

/* Mejoras táctiles para dispositivos touch */
@media (hover: none) and (pointer: coarse) {
  .page-link,
  .goto-btn {
    /* Aumentar área táctil en dispositivos touch */
    padding: 0.5rem;
  }
  
  .page-item {
    margin: 0 1px;
  }
}
</style>
